import { EventTarget } from '../utils/event-target';
import { FlexBuffer, isFlexBufferCompatible } from '../utils/flex-buffer';
import { deserialize, serialize } from '../utils/serialization/serialization';

const CALL_CODE = 1;
const RESPONSE_CODE = 2;

export class Connection extends EventTarget {
    constructor({ webSocket, name = null, expectAnswer = false, onConnect = null, onDisconnect = null }) {
        super({ strict: false });

        this._webSocket = webSocket;
        this._expectAnswer = expectAnswer;
        this._name = name;
        this._pendingCalls = new Map();
        this._nextCallId = 1;
        this._buffer = new FlexBuffer();
        this._connected = false;
        this._resolveReady = null;
        this._ready = new Promise(resolve => this._resolveReady = resolve);
        this._connectCallback = onConnect;
        this._disconnectCallback = onDisconnect;

        this._webSocket.addEventListener('open', () => this._onConnect());
        this._webSocket.addEventListener('close', () => this._onDisconnect());
        this._webSocket.addEventListener('message', message => this._onMessage(message));

        if (this._webSocket.readyState === 1) {
            this._onConnect();
        }
    }

    _onConnect() {
        this._connected = true;
        this._resolveReady();

        if (this._connectCallback) {
            this._connectCallback();
        }
    }

    _onDisconnect() {
        if (this._disconnectCallback) {
            this._disconnectCallback();
        }
    }

    _log(message) {
        console.log(message);
    }

    _onMessage(message) {
        if (typeof Blob !== 'undefined' && message.data instanceof Blob) {
            message.data.arrayBuffer().then(buffer => this._onMessage({ data: new Uint8Array(buffer) }));
            return;
        }

        if (!ArrayBuffer.isView(message.data)) {
            this._log('_onMessage: not a buffer');
            return;
        }

        const buffer = new FlexBuffer(message.data);
        const code = buffer.readUint16();
        const callId = buffer.readUint32();
        const methodName = buffer.readString();
        const payload = deserialize(buffer);

        if (code === CALL_CODE) {
            let result = this.triggerEvent(methodName, payload).find(value => value !== undefined);

            if (callId) {
                if (!(result instanceof Promise)) {
                    result = Promise.resolve(result);
                }

                result.then(value => {
                    this._send(RESPONSE_CODE, callId, methodName, value);
                });
            }
        } else if (code === RESPONSE_CODE) {
            const resolve = this._pendingCalls.get(callId);

            if (resolve) {
                // TODO: remove eventually
                if (payload && payload.error) {
                    console.log(`ERROR (${methodName}): ${payload.error}`);
                }

                this._pendingCalls.delete(callId);
                resolve(payload);
            }
        } else {
            this._log(`_onMessage: invalid code ${CALL_CODE}`);
        }
    }

    _callRemoteMethod(methodName, data) {
        let callId = this._nextCallId++;

        return this._ready.then(() => {
            if (this._expectAnswer) {
                return new Promise(resolve => {
                    this._pendingCalls.set(callId, resolve);
                    this._send(CALL_CODE, callId, methodName, data);
                });
            } else {
                this._send(CALL_CODE, 0, methodName, data);
            }
        });
    }

    _send(code, callId, methodName, data) {
        if (!isFlexBufferCompatible(data)) {
            data = serialize(data);
        }

        this._buffer.reset();
        this._buffer.pushUint16(code);
        this._buffer.pushUint32(callId);
        this._buffer.pushString(methodName);
        this._buffer.pushBuffer(data);

        this._webSocket.send(this._buffer.arrayBuffer());
    }

    query(methodName, data) {
        return this._callRemoteMethod(methodName, data);
    }
}